import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import { useSocket } from '../../context/SocketContext.js';
import MessageModal from '../../components/dashboard/MessageModal.js';
import { FiBookOpen, FiCheckCircle, FiHeart, FiMessageSquare, FiRefreshCw, FiStar, FiX } from 'react-icons/fi';
import { buildApiUrl } from '../../config/api';
import { fetchJson, formatFetchError } from '../../utils/fetchJson';
import { getProfilePhotoUrl } from '../../utils/profileUtils';

const MyMatches = () => {
  const [activeTab, setActiveTab] = useState('potential');
  const [viewingImage, setViewingImage] = useState(null);

  // Initialize state from cache
  const [potentialMatches, setPotentialMatches] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_matches_potential');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });

  const [whoViewedMe, setWhoViewedMe] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_matches_viewed');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });

  const [likedProfiles, setLikedProfiles] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_matches_liked');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });

  const [whoLikedMe, setWhoLikedMe] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_matches_liked_by');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });

  const [newLikesCount, setNewLikesCount] = useState(0);
  const [newViewsCount, setNewViewsCount] = useState(0);
  // Only show loading initially if we have NO data for the current tab
  const [loading, setLoading] = useState(() => {
    const hasPotential = !!localStorage.getItem('cached_matches_potential');
    return !hasPotential;
  });

  const [error, setError] = useState('');
  const [messageModal, setMessageModal] = useState({
    isOpen: false,
    recipient: null
  });
  const [likedProfilesMap, setLikedProfilesMap] = useState(new Set()); // Track liked profile IDs
  const [currentPage, setCurrentPage] = useState(1);
  const profilesPerPage = 15;
  const { user } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchPotentialMatches = useCallback(async () => {
    // Only set loading if we don't have data already

    setError('');

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        console.log('No auth token found');
        return;
      }

      const { response, data } = await fetchJson(buildApiUrl('/api/dashboard/potential-matches'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('Token expired, redirecting to login...');
          navigate('/login');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (data?.success) {
        const matches = Array.isArray(data?.data?.matches) ? data.data.matches : [];
        setPotentialMatches(matches);
        localStorage.setItem('cached_matches_potential', JSON.stringify(matches));

        // Sync local liked state with server source of truth
        setLikedProfilesMap(prev => {
          const next = new Set(prev);
          matches.forEach(p => {
            if (p.isLiked === true) {
              next.add(p.id || p._id);
            } else if (p.isLiked === false) {
              next.delete(p.id || p._id);
            }
          });
          localStorage.setItem('likedProfilesMap', JSON.stringify([...next]));
          return next;
        });

        console.log('🎯 Potential matches fetched:', matches);
      } else {
        setError(data?.message || 'Failed to fetch matches');
      }
    } catch (err) {
      console.error('Error fetching potential matches:', err);
      setError(formatFetchError(err, 'Failed to fetch matches. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [navigate]); // potentialMatches dependency removed to avoid stale closure issues if needed, but safe here

  const fetchWhoViewedMe = useCallback(async () => {

    setError('');

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        console.log('No auth token found');
        return;
      }

      const { response, data } = await fetchJson(buildApiUrl('/api/dashboard/who-viewed-me'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        console.log('Token expired, redirecting to login...');
        navigate('/login');
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (data?.success) {
        const viewers = Array.isArray(data?.data?.viewers) ? data.data.viewers : [];
        setWhoViewedMe(viewers);
        localStorage.setItem('cached_matches_viewed', JSON.stringify(viewers));
        console.log('👀 Who viewed me fetched:', viewers);
      } else {
        setError(data?.message || 'Failed to fetch who viewed me');
      }
    } catch (err) {
      console.error('Error fetching who viewed me:', err);
      setError(formatFetchError(err, 'Failed to fetch who viewed me. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchLikedProfiles = useCallback(async () => {

    setError('');

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        console.log('No auth token found');
        return;
      }

      const { response, data } = await fetchJson(buildApiUrl('/api/dashboard/liked-profiles'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        console.log('Token expired, redirecting to login...');
        navigate('/login');
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (data?.success) {
        const profiles = Array.isArray(data?.data?.profiles) ? data.data.profiles : [];
        setLikedProfiles(profiles);
        localStorage.setItem('cached_matches_liked', JSON.stringify(profiles));

        // Update the liked profiles map with the fetched profiles
        const likedIds = new Set(profiles.filter(p => p).map(p => p.id || p._id));
        setLikedProfilesMap(likedIds);
        // Update localStorage cache
        localStorage.setItem('likedProfilesMap', JSON.stringify([...likedIds]));
        console.log('❤️ Liked profiles fetched:', profiles);
      } else {
        setError(data?.message || 'Failed to fetch liked profiles');
      }
    } catch (err) {
      console.error('Error fetching liked profiles:', err);
      setError(formatFetchError(err, 'Failed to fetch liked profiles. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchWhoLikedMe = useCallback(async () => {

    setError('');

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        console.log('No auth token found');
        return;
      }

      const { response, data } = await fetchJson(buildApiUrl('/api/dashboard/who-liked-me'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('Token expired, redirecting to login...');
          navigate('/login');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (data?.success) {
        const profiles = Array.isArray(data?.data?.profiles) ? data.data.profiles : [];
        setWhoLikedMe(profiles);
        localStorage.setItem('cached_matches_liked_by', JSON.stringify(profiles));
        console.log('💕 Profiles who liked you fetched:', profiles);
      } else {
        setError(data?.message || 'Failed to fetch profiles who liked you');
      }
    } catch (err) {
      console.error('Error fetching who liked me:', err);
      setError(formatFetchError(err, 'Failed to fetch profiles who liked you. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const { response, data } = await fetchJson(buildApiUrl('/api/dashboard/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok && data?.success) {
        setNewLikesCount(data.data.newLikesCount || 0);
        setNewViewsCount(data.data.newViewsCount || 0);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  // Listen for real-time notifications
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (notification) => {
      // If we're on the whoViewed tab and get a new view notification, refresh the list
      if (activeTab === 'whoViewed' && notification.type === 'new_view') {
        console.log('🔔 New view received, refreshing list...');
        fetchWhoViewedMe();
      } else if (activeTab === 'whoLiked' && notification.type === 'profile_like') {
        console.log('🔔 New like received, refreshing list...');
        fetchWhoLikedMe();
      }

      // Always refresh stats to update badges
      if (notification.type === 'new_view' || notification.type === 'profile_like') {
        fetchStats();
      }
    };

    socket.on('real_time_notification', handleNotification);

    return () => {
      socket.off('real_time_notification', handleNotification);
    };
  }, [socket, activeTab, fetchWhoViewedMe, fetchWhoLikedMe, fetchStats]);

  // Fetch potential matches on component mount
  useEffect(() => {
    fetchPotentialMatches();
    fetchStats();
    fetchLikedProfiles(); // Also fetch liked profiles to initialize the map

    // Load liked profiles from localStorage for instant UI
    const cachedLikedMap = localStorage.getItem('likedProfilesMap');
    if (cachedLikedMap) {
      try {
        const likedIds = JSON.parse(cachedLikedMap);
        setLikedProfilesMap(new Set(likedIds));
      } catch (error) {
        console.log('Error parsing cached liked profiles:', error);
      }
    }
  }, [fetchPotentialMatches, fetchLikedProfiles, fetchStats]);

  // Reset pagination when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'whoViewed') {
      // If we have new views, we MUST fetch to clear the count on server
      if (whoViewedMe.length === 0 || newViewsCount > 0) {
        fetchWhoViewedMe().then(() => {
          // Notify sidebar to refresh stats
          window.dispatchEvent(new Event('dashboard_stats_updated'));
        });
      }
      setNewViewsCount(0); // Optimistically clear notification
    } else if (tab === 'liked' && likedProfiles.length === 0) {
      fetchLikedProfiles();
    } else if (tab === 'whoLiked') {
      // If we have new likes, we MUST fetch to clear the count on server
      if (whoLikedMe.length === 0 || newLikesCount > 0) {
        fetchWhoLikedMe().then(() => {
          // Notify sidebar to refresh stats
          window.dispatchEvent(new Event('dashboard_stats_updated'));
        });
      }
      setNewLikesCount(0); // Optimistically clear notification
    }
  };

  const currentList = activeTab === 'potential' ? potentialMatches :
    activeTab === 'whoViewed' ? whoViewedMe :
      activeTab === 'liked' ? likedProfiles : whoLikedMe;

  // Get paginated profiles (show only current page items)
  const displayedProfiles = currentList.slice(0, currentPage * profilesPerPage);

  // Check if there are more profiles to load
  const hasMoreProfiles = currentList.length > currentPage * profilesPerPage;

  // Load more profiles
  const handleLoadMore = () => {
    if (hasMoreProfiles) {
      setCurrentPage(prev => prev + 1);
    }
  };



  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const birthDate = new Date(dateOfBirth);
    if (Number.isNaN(birthDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const normalizeAge = (value) => {
    if (value === null || value === undefined) return null;
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    if (!Number.isFinite(num) || num <= 0) return null;
    return num;
  };

  const getProfileAge = (profile) => {
    const directAge = normalizeAge(profile?.age);
    if (directAge) return directAge;
    return normalizeAge(calculateAge(profile?.dateOfBirth || profile?.dob));
  };

  const formatLocation = (country, city) => {
    if (country) return country;
    if (city) return city;
    return 'Location not specified';
  };

  const handleMessageUser = (match) => {
    // Open message modal instead of navigating
    setMessageModal({
      isOpen: true,
      recipient: match
    });
  };

  const handleCloseMessageModal = () => {
    setMessageModal({
      isOpen: false,
      recipient: null
    });
  };

  const handleSendMessage = (newMessage) => {
    // This will be called when a message is sent successfully
    console.log('📨 Message sent:', newMessage);
  };

  const handleViewProfile = (match) => {
    // Navigate to full profile view
    const userId = match.id || match._id;
    navigate(`/dashboard/user-profile/${userId}`, { state: { from: location.pathname + location.search } });
  };

  const handleLikeProfile = async (match) => {
    const profileId = match.id || match._id;
    const isCurrentlyLiked = likedProfilesMap.has(profileId);

    try {
      // Optimistic UI update
      const newLikedMap = new Set(likedProfilesMap);
      if (isCurrentlyLiked) {
        newLikedMap.delete(profileId);
        console.log('💔 Profile unliked (instant)');
      } else {
        newLikedMap.add(profileId);
        console.log('❤️ Profile liked (instant)');
      }
      setLikedProfilesMap(newLikedMap);

      // Save to localStorage for persistence
      localStorage.setItem('likedProfilesMap', JSON.stringify([...newLikedMap]));

      // Send to server
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl(`/api/user-profiles/${profileId}/like`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && data.success) {
        console.log('✅ Server confirmed like action');
        // Update liked profiles array if needed
        if (!isCurrentlyLiked) {
          setLikedProfiles(prev => [...prev, match]);
        } else {
          setLikedProfiles(prev => prev.filter(p => (p.id || p._id) !== profileId));
        }
      } else {
        // Server failed - revert the optimistic update
        setLikedProfilesMap(likedProfilesMap);
        localStorage.setItem('likedProfilesMap', JSON.stringify([...likedProfilesMap]));
        console.error('Server rejected like action:', data.message);
        setError(data.message || 'Failed to update like status');
      }
    } catch (err) {
      // Network error - revert the optimistic update
      setLikedProfilesMap(likedProfilesMap);
      localStorage.setItem('likedProfilesMap', JSON.stringify([...likedProfilesMap]));
      console.error('Error liking profile:', err);
      setError(formatFetchError(err, 'Failed to update like status. Please try again.'));
    }
  };

  return (
    <>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-romantic font-bold text-gray-900 mb-2">
            My Matches
          </h1>
          <p className="text-gray-600">
            Discover potential matches based on your preferences. View profiles,
            send messages, and find your perfect match!
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6 border-b border-gray-200">
          <button
            type="button"
            onClick={() => handleTabChange('potential')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${activeTab === 'potential'
              ? 'border-romantic-500 text-romantic-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            Potential Matches
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('whoViewed')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all flex items-center gap-2 ${activeTab === 'whoViewed'
              ? 'border-romantic-500 text-romantic-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            Who Viewed Me
            {newViewsCount > 0 && (
              <span className="bg-romantic-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {newViewsCount > 9 ? '9+' : newViewsCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('liked')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${activeTab === 'liked'
              ? 'border-romantic-500 text-romantic-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            Liked Profiles
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('whoLiked')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${activeTab === 'whoLiked'
              ? 'border-romantic-500 text-romantic-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            Who Liked Me
            {newLikesCount > 0 && (
              <span className="bg-romantic-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {newLikesCount > 9 ? '9+' : newLikesCount}
              </span>
            )}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-romantic-500"></div>
            <span className="ml-3 text-gray-600">Finding matches for you...</span>
          </div>
        )}

        {/* Summary */}
        {!loading && !error && (
          <div className="mb-4 text-sm text-gray-600">
            Showing{' '}
            <span className="font-semibold text-gray-900">
              {displayedProfiles.length}
            </span>
            {' '}of{' '}
            <span className="font-semibold text-gray-900">
              {currentList.length}
            </span>{' '}
            {activeTab === 'potential' ? 'potential matches' :
              activeTab === 'whoViewed' ? 'people who viewed you' :
                activeTab === 'liked' ? 'liked profiles' : 'people who liked you'}
          </div>
        )}

        {/* Cards grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayedProfiles.filter(p => p).map((profile) => {
              const profileId = profile.id || profile._id;
              const age = getProfileAge(profile);

              return (
                <div
                  key={profileId}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex flex-col hover:shadow-xl transition-shadow"
                >
                  <div className="relative h-[320px] w-full">
                    <img
                      src={getProfilePhotoUrl(profile)}
                      alt={`${profile.firstName} ${profile.lastName}`}
                      className="h-full w-full object-cover cursor-pointer"
                      onClick={() => setViewingImage(getProfilePhotoUrl(profile))}
                    />
                    <div className="absolute top-3 left-3 bg-white/90 text-xs px-3 py-1 rounded-full text-gray-700 shadow">
                      {formatLocation(profile.country, profile.city)}
                    </div>
                    <div className="absolute top-3 right-3 bg-romantic-500 text-xs px-3 py-1 rounded-full text-white shadow">
                      {profile.compatibilityScore || Math.floor(Math.random() * 20 + 80)}% Match
                    </div>
                  </div>

                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {profile.firstName} {profile.lastName}{age ? `, ${age}` : ''}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {profile.profession || 'Not specified'}
                        </p>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="mb-3 text-sm text-gray-600">
                      <div className="flex items-center gap-2 mb-1">
                        <FiBookOpen className="text-gray-400" size={14} />
                        <span>{profile.education || 'Education not specified'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FiStar className="text-gray-400" size={14} />
                        <span>{profile.religion || 'Religion not specified'}</span>
                      </div>
                    </div>

                    <div className="mt-3 mb-4">
                      <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                        <div
                          className="h-2.5 rounded-full bg-gradient-to-r from-romantic-500 to-primary-500"
                          style={{ width: `${profile.compatibilityScore || Math.floor(Math.random() * 20 + 80)}%` }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Compatibility Alignment
                      </p>
                    </div>

                    <div className="mt-auto flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => handleMessageUser(profile)}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-romantic-500 to-primary-500 hover:from-romantic-600 hover:to-primary-600 shadow-md hover:shadow-lg transition-all"
                      >
                        <FiMessageSquare size={16} className="mr-2" />
                        Message
                      </button>
                      <button
                        type="button"
                        onClick={() => handleViewProfile(profile)}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg border border-romantic-200 text-romantic-600 bg-rose-50 hover:bg-rose-100 transition-colors"
                      >
                        View Profile
                      </button>
                      <button
                        type="button"
                        onClick={() => handleLikeProfile(profile)}
                        className={`p-2.5 rounded-lg transition-all ${likedProfilesMap.has(profile.id || profile._id)
                          ? 'bg-romantic-500 text-white shadow-md'
                          : 'bg-gray-100 text-gray-500 hover:bg-romantic-100 hover:text-romantic-500'
                          }`}
                        title={likedProfilesMap.has(profile.id || profile._id) ? 'Unlike' : 'Like'}
                      >
                        <FiHeart className={likedProfilesMap.has(profile.id || profile._id) ? 'fill-current' : ''} size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {displayedProfiles.length === 0 && currentList.length > 0 && (
              <div className="col-span-full text-center py-12 text-gray-500">
                <div className="flex justify-center text-5xl mb-4 text-romantic-400"><FiRefreshCw /></div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">Loading profiles...</h3>
                <p className="text-gray-600">Please wait while we load more profiles for you.</p>
              </div>
            )}

            {currentList.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-500">
                <div className="flex justify-center text-5xl mb-4 text-romantic-400"><FiHeart /></div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">
                  {activeTab === 'potential' ? 'No potential matches yet' :
                    activeTab === 'whoViewed' ? 'No views yet' :
                      activeTab === 'liked' ? 'No liked profiles yet' : 'No one has liked you yet'}
                </h3>
                <p className="text-gray-600">
                  {activeTab === 'potential' ? 'Keep exploring to find your perfect match!' :
                    activeTab === 'whoViewed' ? 'Optimize your profile to get more views!' :
                      activeTab === 'liked' ? 'Start liking profiles to see them here!' : 'Complete your profile and start connecting!'}
                </p>
              </div>
            )}

            {/* Load More Button */}
            {hasMoreProfiles && displayedProfiles.length > 0 && (
              <div className="col-span-full text-center py-6">
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-3 bg-romantic-500 text-white rounded-lg hover:bg-romantic-600 transition-colors font-semibold shadow-md hover:shadow-lg"
                >
                  Load More Profiles
                </button>
              </div>
            )}

            {/* No More Profiles Message */}
            {!hasMoreProfiles && displayedProfiles.length > 0 && (
              <div className="col-span-full text-center py-6 text-gray-500">
                <div className="text-4xl mb-2 text-romantic-400"><FiCheckCircle /></div>
                <p className="text-sm">You've seen all {activeTab === 'potential' ? 'potential matches' :
                  activeTab === 'whoViewed' ? 'people who viewed you' :
                    activeTab === 'liked' ? 'liked profiles' : 'people who liked you'}!</p>
              </div>
            )}
          </div>
        )}
      </div >

      {/* Message Modal */}
      <MessageModal
        isOpen={messageModal.isOpen}
        onClose={handleCloseMessageModal}
        recipient={messageModal.recipient}
        currentUser={user}
        onSendMessage={handleSendMessage}
      />

      {/* Full Screen Image Viewer */}
      {viewingImage && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center p-4" onClick={() => setViewingImage(null)}>
          <button
            onClick={() => setViewingImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2 rounded-full bg-black/50 transition-colors"
          >
            <FiX size={32} />
          </button>
          <img
            src={viewingImage}
            alt="Full size profile"
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default MyMatches;
